import { useState, useEffect, useCallback } from 'react';
import { api, getErrorMessage } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { Shield, Plus, Edit, Copy, Users, Check } from 'lucide-react';

export default function RolesManagement({ user }) {
  const [activeTab, setActiveTab] = useState('system');
  const [systemPermissions, setSystemPermissions] = useState({});
  const [rolePermissions, setRolePermissions] = useState({});
  const [customRoles, setCustomRoles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [roleDialog, setRoleDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', permissions: {} });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [permsRes, rolesRes, customRes] = await Promise.all([
        api.get('/permissions/system'),
        api.get('/permissions/roles'),
        api.get('/custom-roles')
      ]);
      setSystemPermissions(permsRes.data.modules || {});
      setRolePermissions(rolesRes.data || {});
      setCustomRoles(customRes.data || []);
    } catch (err) {
      toast.error('Failed to load permissions data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSaveRole = async () => {
    try {
      if (selectedRole) {
        await api.put(`/custom-roles/${selectedRole.id}`, roleForm);
        toast.success('Role updated');
      } else {
        await api.post('/custom-roles', roleForm);
        toast.success('Role created');
      }
      setRoleDialog(false);
      resetRoleForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleCloneRole = async (role) => {
    try {
      await api.post(`/custom-roles/${role.id}/clone?new_name=${role.name} (Copy)`);
      toast.success('Role cloned');
      fetchData();
    } catch (err) {
      toast.error('Failed to clone role');
    }
  };

  const resetRoleForm = () => {
    setSelectedRole(null);
    setRoleForm({ name: '', description: '', permissions: {} });
  };

  const openEditRole = (role) => {
    setSelectedRole(role);
    setRoleForm({ name: role.name, description: role.description || '', permissions: role.permissions || {} });
    setRoleDialog(true);
  };

  const togglePermission = (module, feature) => {
    const perms = { ...roleForm.permissions };
    if (!perms[module]) perms[module] = {};
    perms[module][feature] = !perms[module][feature];
    setRoleForm({ ...roleForm, permissions: perms });
  };

  const toggleModuleAll = (module) => {
    const perms = { ...roleForm.permissions };
    const features = systemPermissions[module]?.features || {};
    const allEnabled = Object.keys(features).every(f => perms[module]?.[f]);
    perms[module] = {};
    Object.keys(features).forEach(f => { perms[module][f] = !allEnabled; });
    setRoleForm({ ...roleForm, permissions: perms });
  };

  const hasPermission = (perms, module, feature) => {
    if (perms?.['*']?.['*']) return true;
    if (perms?.[module]?.['*']) return true;
    return perms?.[module]?.[feature] || false;
  };

  return (
    <div className="p-6 space-y-6" data-testid="roles-management">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roles & Permissions</h1>
          <p className="text-gray-500">Manage system roles and custom permissions</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-orange-50">
          <TabsTrigger value="system" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white"><Shield className="h-4 w-4 mr-2" />System Roles</TabsTrigger>
          <TabsTrigger value="custom" className="data-[state=active]:bg-orange-500 data-[state=active]:text-white"><Users className="h-4 w-4 mr-2" />Custom Roles</TabsTrigger>
        </TabsList>

        {/* System Roles Tab */}
        <TabsContent value="system">
          <Card>
            <CardHeader>
              <CardTitle>System Role Permissions</CardTitle>
              <CardDescription>Default permissions for built-in roles</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="sticky left-0 bg-white">Module / Feature</TableHead>
                      {Object.keys(rolePermissions).map(role => (
                        <TableHead key={role} className="text-center min-w-24">
                          <Badge variant="outline">{role.replace('_', ' ')}</Badge>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(systemPermissions).map(([module, moduleData]) => (
                      <>
                        <TableRow key={module} className="bg-gray-50">
                          <TableCell className="font-semibold sticky left-0 bg-gray-50">{moduleData.name}</TableCell>
                          {Object.keys(rolePermissions).map(role => (
                            <TableCell key={role} className="text-center">
                              {hasPermission(rolePermissions[role]?.permissions, module, '*') && (
                                <Badge className="bg-green-500">All</Badge>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                        {Object.entries(moduleData.features || {}).map(([feature, featureName]) => (
                          <TableRow key={`${module}-${feature}`}>
                            <TableCell className="pl-8 text-sm text-gray-600 sticky left-0 bg-white">{featureName}</TableCell>
                            {Object.keys(rolePermissions).map(role => (
                              <TableCell key={role} className="text-center">
                                {hasPermission(rolePermissions[role]?.permissions, module, feature) && (
                                  <Check className="h-4 w-4 text-green-500 mx-auto" />
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Roles Tab */}
        <TabsContent value="custom">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Custom Roles</CardTitle>
                <CardDescription>Create and manage custom permission sets</CardDescription>
              </div>
              <Button onClick={() => { resetRoleForm(); setRoleDialog(true); }} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="h-4 w-4 mr-2" />Create Role
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Level</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customRoles.map(role => (
                    <TableRow key={role.id}>
                      <TableCell className="font-medium">{role.name}</TableCell>
                      <TableCell className="text-gray-500">{role.description || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{role.hotel_id ? 'Hotel' : role.company_id ? 'Company' : 'Global'}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={role.is_active ? 'bg-green-500' : 'bg-gray-500'}>
                          {role.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEditRole(role)} title="Edit">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleCloneRole(role)} title="Clone">
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {customRoles.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No custom roles defined yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Role Dialog */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedRole ? 'Edit' : 'Create'} Custom Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Role Name *</Label>
                <Input value={roleForm.name} onChange={(e) => setRoleForm({...roleForm, name: e.target.value})} placeholder="e.g., Night Auditor" />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={roleForm.description} onChange={(e) => setRoleForm({...roleForm, description: e.target.value})} placeholder="Brief description" />
              </div>
            </div>

            <div>
              <Label className="text-lg font-semibold">Permissions</Label>
              <div className="mt-4 space-y-4">
                {Object.entries(systemPermissions).map(([module, moduleData]) => (
                  <Card key={module} className="border-gray-200">
                    <CardHeader className="py-3 cursor-pointer" onClick={() => toggleModuleAll(module)}>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">{moduleData.name}</CardTitle>
                        <Checkbox checked={Object.keys(moduleData.features || {}).every(f => roleForm.permissions?.[module]?.[f])} />
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(moduleData.features || {}).map(([feature, featureName]) => (
                          <div key={feature} className="flex items-center space-x-2">
                            <Checkbox 
                              id={`${module}-${feature}`}
                              checked={roleForm.permissions?.[module]?.[feature] || false}
                              onCheckedChange={() => togglePermission(module, feature)}
                            />
                            <Label htmlFor={`${module}-${feature}`} className="text-sm cursor-pointer">{featureName}</Label>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveRole} className="bg-orange-500 hover:bg-orange-600">
              {selectedRole ? 'Update' : 'Create'} Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
